import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtPct } from "../lib/api";
import { haptic } from "../lib/mobile";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "../components/ui/alert-dialog";
import { ArrowLeft, TrendingUp, TrendingDown, Info, Search, Star, StarOff, Plus, X, Layers } from "lucide-react";
import { toast } from "sonner";

const POPULAR = ["AAPL", "MSFT", "GOOGL", "AMZN", "TSLA", "NVDA", "META", "NFLX", "AMD", "SPY", "QQQ", "GLD"];

// Tiny sparkline used inline in the IV cell — renders the underlying's 30-day
// rolling annualized realized volatility (proxy for chain-level IV trend).
function IvSpark({ series }) {
  if (!series || series.length < 2) return null;
  const w = 40, h = 14, pad = 1;
  const min = Math.min(...series), max = Math.max(...series);
  const range = Math.max(max - min, 0.001);
  const pts = series.map((v, i) => {
    const x = pad + (i * (w - 2 * pad)) / (series.length - 1);
    const y = h - pad - ((v - min) * (h - 2 * pad)) / range;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const last = series[series.length - 1];
  const first = series[0];
  const rising = last >= first;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="shrink-0" aria-label="30d HV" data-testid="iv-sparkline">
      <polyline
        points={pts} fill="none" strokeWidth="1.2"
        stroke={rising ? "hsl(var(--loss))" : "hsl(var(--gain))"}
      />
    </svg>
  );
}

export default function Options() {
  const params = useParams();
  const navigate = useNavigate();
  const [symbol, setSymbol] = useState((params.symbol || "AAPL").toUpperCase());
  const [input, setInput] = useState(symbol);
  const [recent, setRecent] = useState(() => {
    try { return JSON.parse(localStorage.getItem("optionsRecent") || "[]"); } catch { return []; }
  });
  const [expirations, setExpirations] = useState([]);
  const [expiration, setExpiration] = useState(null);
  const [chain, setChain] = useState(null);
  const [side, setSide] = useState("C");
  const [selected, setSelected] = useState(null);   // { row, side }
  const [qty, setQty] = useState(1);
  const [placing, setPlacing] = useState(false);
  const [route, setRoute] = useState("sim");
  const [reviewSide, setReviewSide] = useState(null); // "buy" | "sell" | null
  const [loadingExps, setLoadingExps] = useState(false);
  const [chainError, setChainError] = useState(null);   // sticky error banner
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [favs, setFavs] = useState(() => {
    try { return JSON.parse(localStorage.getItem("optionsFavs") || "[]"); } catch { return []; }
  });

  // Multi-leg spread builder
  const [legs, setLegs] = useState([]);
  const [spreadAnalysis, setSpreadAnalysis] = useState(null);
  const [analysing, setAnalysing] = useState(false);
  const [placingSpread, setPlacingSpread] = useState(false);

  const addLeg = (row, kind) => {
    const midPrice = (row.bid + row.ask) / 2 || row.last;
    setLegs((cur) => [...cur, {
      kind,                       // "C" | "P"
      strike: row.strike,
      action: "buy",
      qty: 1,
      price: Number(midPrice.toFixed(2)),
      contract_symbol: row.contract_symbol,
      expiration,
    }]);
    toast.success(`Added ${row.strike} ${kind === "C" ? "call" : "put"} to spread`);
  };
  const updateLeg = (i, patch) => setLegs((cur) => cur.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const removeLeg = (i) => setLegs((cur) => cur.filter((_, idx) => idx !== i));
  const clearLegs = () => { setLegs([]); setSpreadAnalysis(null); };

  const saveFavs = (next) => { setFavs(next); localStorage.setItem("optionsFavs", JSON.stringify(next)); };
  const isFav = expiration && favs.some((f) => f.symbol === symbol && f.expiration === expiration);

  const toggleFav = () => {
    if (!expiration) return;
    if (isFav) saveFavs(favs.filter((f) => !(f.symbol === symbol && f.expiration === expiration)));
    else saveFavs([{ symbol, expiration }, ...favs].slice(0, 12));
  };

  const pickSymbol = (s) => {
    const up = (s || "").trim().toUpperCase();
    if (!up) return;
    setSymbol(up); setInput(up); setSelected(null);
    setRecent((prev) => {
      const next = [up, ...prev.filter((x) => x !== up)].slice(0, 8);
      localStorage.setItem("optionsRecent", JSON.stringify(next));
      return next;
    });
  };

  useEffect(() => {
    setChain(null); setExpiration(null); setLoadingExps(true);
    api.get(`/options/${symbol}/expirations`)
      .then(({ data }) => {
        setExpirations(data.expirations || []);
        if (data.expirations?.length) setExpiration(data.expirations[0]);
        else toast.error(`No listed options for ${symbol}`);
      })
      .catch(() => toast.error(`No listed options for ${symbol}`))
      .finally(() => setLoadingExps(false));
  }, [symbol]);

  useEffect(() => {
    if (!input || input.length < 1) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/options/search?q=${encodeURIComponent(input)}`);
        setSuggestions(data);
      } catch { setSuggestions([]); }
    }, 150);
    return () => clearTimeout(t);
  }, [input]);

  useEffect(() => {
    if (!symbol || !expiration) return;
    api.get(`/options/${symbol}/chain?expiration=${expiration}`).then(({ data }) => setChain(data)).catch(() => {});
  }, [symbol, expiration]);

  const submit = async (action) => {
    if (!selected) return toast.error("Pick a contract");
    setPlacing(true);
    try {
      const opt = selected.row;
      const { data } = await api.post("/orders", {
        symbol,
        asset_type: "option",
        side: action,
        quantity: Number(qty),
        route,
        option: {
          contract_symbol: opt.contract_symbol,
          strike: opt.strike, expiration, type: selected.side,
        },
      });
      haptic(action);
      const dir = selected.side === "C" ? "Call" : "Put";
      // Undo toast
      const undoBody = {
        symbol, asset_type: "option",
        side: action === "buy" ? "sell" : "buy",
        quantity: Number(qty), route,
        option: {
          contract_symbol: opt.contract_symbol,
          strike: opt.strike, expiration, type: selected.side,
        },
      };
      toast.success(
        `${action.toUpperCase()} ${qty} ${symbol} ${opt.strike} ${dir} @ $${(data.order.price / 100).toFixed(2)}`,
        { duration: 8000, action: {
          label: "Undo",
          onClick: async () => {
            try { await api.post("/orders", undoBody); toast.info("Order reversed"); }
            catch { toast.error("Undo failed"); }
          },
        }}
      );
      setSelected(null); setQty(1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Order failed");
    } finally { setPlacing(false); setReviewSide(null); }
  };

  const estPremium = selected
    ? ((selected.row.bid + selected.row.ask) / 2 || selected.row.last) * 100 * Number(qty || 0)
    : 0;

  const rows = chain ? (side === "C" ? chain.calls : chain.puts) : [];
  const spot = chain?.spot;

  // Analyse the current spread whenever legs change
  useEffect(() => {
    if (!legs.length || !spot) { setSpreadAnalysis(null); return; }
    setAnalysing(true);
    const timer = setTimeout(() => {
      api.post("/options/spread/analyse", { symbol, spot, legs })
        .then((r) => setSpreadAnalysis(r.data))
        .catch(() => setSpreadAnalysis(null))
        .finally(() => setAnalysing(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [legs, spot, symbol]);
  const nearAtm = spot ? rows
    .map((r, i) => ({ r, i, dist: Math.abs(r.strike - spot) }))
    .sort((a, b) => a.dist - b.dist)[0]?.i : -1;

  return (
    <div className="space-y-6" data-testid="options-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-2">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Options</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">{symbol} chain</h1>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
          <form onSubmit={(e) => { e.preventDefault(); pickSymbol(input); setShowSug(false); }} className="flex items-center gap-2 relative">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={input}
                     onChange={(e) => { setInput(e.target.value.toUpperCase()); setShowSug(true); }}
                     onFocus={() => setShowSug(true)}
                     onBlur={() => setTimeout(() => setShowSug(false), 150)}
                     placeholder="Ticker (e.g. TSLA)"
                     data-testid="options-ticker-input"
                     className="pl-9 pr-3 rounded-full h-9 w-40 uppercase font-mono-tab" />
              {showSug && suggestions.length > 0 && (
                <div className="absolute top-full mt-1 left-0 right-0 bg-card border border-border rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto"
                     data-testid="options-suggestions">
                  {suggestions.map((s) => (
                    <button type="button" key={s.symbol}
                            onMouseDown={(e) => { e.preventDefault(); pickSymbol(s.symbol); setShowSug(false); }}
                            data-testid={`suggestion-${s.symbol}`}
                            className="w-full text-left px-3 py-1.5 hover:bg-secondary/60 font-mono-tab text-sm">
                      {s.symbol}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button type="submit" size="sm" className="rounded-full" data-testid="options-load-btn"
                    disabled={loadingExps || !input.trim()}>
              {loadingExps ? "Loading..." : "Load"}
            </Button>
          </form>
          <select value={expiration || ""} onChange={(e) => setExpiration(e.target.value)}
                  data-testid="options-exp" disabled={!expirations.length}
                  className="bg-secondary border border-border rounded-full text-sm px-3 py-1.5">
            {expirations.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
          {expiration && (
            <button onClick={toggleFav} data-testid="fav-toggle"
                    className={`rounded-full p-2 border transition-colors ${isFav ? "bg-[hsl(var(--ai))] text-black border-[hsl(var(--ai))]" : "border-border hover:border-foreground/40"}`}>
              {isFav ? <Star className="w-4 h-4" /> : <StarOff className="w-4 h-4" />}
            </button>
          )}
        </div>
      </div>

      {/* Ticker chips */}
      <div className="flex flex-wrap gap-1.5" data-testid="options-chips">
        {favs.length > 0 && (
          <>
            <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--ai))] self-center mr-1 flex items-center gap-1">
              <Star className="w-3 h-3" /> Favs
            </span>
            {favs.map((f) => (
              <button key={`${f.symbol}-${f.expiration}`}
                      onClick={() => { pickSymbol(f.symbol); setTimeout(() => setExpiration(f.expiration), 200); }}
                      data-testid={`chip-fav-${f.symbol}-${f.expiration}`}
                      className={`px-2.5 py-1 rounded-full text-xs border transition-colors flex items-center gap-1 ${symbol === f.symbol && expiration === f.expiration ? "bg-[hsl(var(--ai))]/20 border-[hsl(var(--ai))]" : "border-[hsl(var(--ai))]/40 hover:bg-[hsl(var(--ai))]/10"}`}>
                <span className="font-medium">{f.symbol}</span>
                <span className="text-[10px] opacity-70">{f.expiration.slice(5)}</span>
              </button>
            ))}
            <span className="w-px bg-border self-stretch mx-1" />
          </>
        )}
        {recent.length > 0 && (
          <>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground self-center mr-1">Recent</span>
            {recent.map((s) => (
              <button key={`r-${s}`} onClick={() => pickSymbol(s)}
                      data-testid={`chip-recent-${s}`}
                      className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${symbol === s ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground/40"}`}>
                {s}
              </button>
            ))}
            <span className="w-px bg-border self-stretch mx-1" />
          </>
        )}
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground self-center mr-1">Popular</span>
        {POPULAR.map((s) => (
          <button key={s} onClick={() => pickSymbol(s)}
                  data-testid={`chip-popular-${s}`}
                  className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${symbol === s ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground/40"}`}>
            {s}
          </button>
        ))}
      </div>

      {chain && (
        <Card className="p-4 border-border rounded-xl">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase text-muted-foreground">Spot</span>
              <span className="font-mono-tab font-medium">{fmtMoney(spot)}</span>
              <Badge variant="outline" className="rounded-full text-[10px]">{chain.days_to_expiry} days</Badge>
            </div>
            <Tabs value={side} onValueChange={setSide}>
              <TabsList data-testid="side-tabs">
                <TabsTrigger value="C" data-testid="tab-calls"><TrendingUp className="w-3 h-3 mr-1" /> Calls</TabsTrigger>
                <TabsTrigger value="P" data-testid="tab-puts"><TrendingDown className="w-3 h-3 mr-1" /> Puts</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className="overflow-x-auto -mx-4 md:mx-0">
            <div className="inline-block min-w-full">
              <div className="grid grid-cols-9 sm:grid-cols-9 px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground bg-secondary/40">
                <div>Strike</div><div className="text-right">Last</div><div className="text-right">Bid</div><div className="text-right">Ask</div>
                <div className="text-right">IV% · 30d</div><div className="text-right">Δ</div><div className="text-right">Γ</div><div className="text-right">Vol</div>
                <div className="text-right">Add</div>
              </div>
              <div className="divide-y divide-border">
                {rows.map((r, i) => (
                  <div key={r.contract_symbol}
                       onClick={() => setSelected({ row: r, side })}
                       data-testid={`chain-row-${r.contract_symbol}`}
                       className={`grid grid-cols-9 items-center px-4 py-2 text-sm cursor-pointer transition-colors font-mono-tab
                                   ${selected?.row?.contract_symbol === r.contract_symbol ? "bg-secondary" : "hover:bg-secondary/50"}
                                   ${i === nearAtm ? "border-l-2 border-l-[hsl(var(--ai))]" : ""}
                                   ${r.in_the_money ? "text-foreground" : "text-muted-foreground"}`}>
                    <div className="font-medium text-foreground">{r.strike}</div>
                    <div className="text-right">{r.last.toFixed(2)}</div>
                    <div className="text-right">{r.bid.toFixed(2)}</div>
                    <div className="text-right">{r.ask.toFixed(2)}</div>
                    <div className="text-right flex items-center justify-end gap-1.5" data-testid={`iv-cell-${r.contract_symbol}`}>
                      <span>{r.iv.toFixed(1)}</span>
                      {chain?.hv_30d?.length > 1 && (
                        <IvSpark series={chain.hv_30d} />
                      )}
                    </div>
                    <div className="text-right">{r.delta.toFixed(2)}</div>
                    <div className="text-right">{r.gamma.toFixed(3)}</div>
                    <div className="text-right">{r.volume}</div>
                    <div className="text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); addLeg(r, side); }}
                        data-testid={`add-leg-${r.contract_symbol}`}
                        title="Add to multi-leg builder"
                        className="p-1 rounded-md hover:bg-[hsl(var(--ai))]/20 text-[hsl(var(--ai))]">
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
                {!rows.length && <div className="text-center text-sm text-muted-foreground py-6">No contracts.</div>}
              </div>
            </div>
          </div>
        </Card>
      )}

      {legs.length > 0 && (
        <Card className="p-5 border-border rounded-xl bg-gradient-to-br from-[hsl(var(--ai))]/5 to-transparent" data-testid="spread-builder">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-[hsl(var(--ai))]" />
              <div className="font-display font-semibold">Spread Builder</div>
              {spreadAnalysis?.label && (
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[hsl(var(--ai))]/15 text-[hsl(var(--ai))]" data-testid="spread-label">
                  {spreadAnalysis.label}
                </span>
              )}
              {analysing && <span className="text-[10px] text-muted-foreground">analysing…</span>}
            </div>
            <button onClick={clearLegs} data-testid="spread-clear"
                    className="text-xs text-muted-foreground hover:text-foreground">Clear all</button>
          </div>

          <div className="space-y-2 mb-4">
            {legs.map((L, i) => (
              <div key={i} className="grid grid-cols-12 items-center gap-2 p-2 rounded-md border border-border bg-card/50" data-testid={`leg-${i}`}>
                <div className="col-span-2 flex items-center gap-1">
                  <button onClick={() => updateLeg(i, { action: L.action === "buy" ? "sell" : "buy" })}
                          data-testid={`leg-action-${i}`}
                          className={`text-[11px] uppercase font-medium px-2 py-1 rounded-full ${L.action === "buy" ? "bg-[hsl(var(--gain))]/15 gain" : "bg-[hsl(var(--loss))]/15 loss"}`}>
                    {L.action}
                  </button>
                </div>
                <div className="col-span-2 font-mono-tab text-sm">
                  <span className="text-muted-foreground text-[10px] uppercase">Qty </span>
                  <Input type="number" min="1" step="1" value={L.qty}
                         onChange={(e) => updateLeg(i, { qty: parseInt(e.target.value) || 1 })}
                         data-testid={`leg-qty-${i}`} className="inline-block w-16 h-7 font-mono-tab text-sm" />
                </div>
                <div className="col-span-3 font-mono-tab text-sm">
                  {L.strike} {L.kind === "C" ? "call" : "put"}
                  <div className="text-[10px] text-muted-foreground">{L.expiration}</div>
                </div>
                <div className="col-span-3 font-mono-tab text-sm">
                  <span className="text-muted-foreground text-[10px] uppercase">@ </span>
                  <Input type="number" min="0" step="0.01" value={L.price}
                         onChange={(e) => updateLeg(i, { price: parseFloat(e.target.value) || 0 })}
                         data-testid={`leg-price-${i}`} className="inline-block w-20 h-7 font-mono-tab text-sm" />
                </div>
                <div className="col-span-2 text-right">
                  <button onClick={() => removeLeg(i)} data-testid={`leg-remove-${i}`}
                          className="p-1 text-muted-foreground hover:text-[hsl(var(--loss))]">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {spreadAnalysis && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm" data-testid="spread-summary">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {spreadAnalysis.net >= 0 ? "Net debit (paid)" : "Net credit (received)"}
                </div>
                <div className={`font-mono-tab text-lg ${spreadAnalysis.net >= 0 ? "loss" : "gain"}`}>
                  {spreadAnalysis.net >= 0 ? "−" : "+"}{fmtMoney(Math.abs(spreadAnalysis.net))}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Max profit</div>
                <div className="font-mono-tab text-lg gain" data-testid="spread-max-profit">
                  {spreadAnalysis.max_profit == null ? "unbounded" : fmtMoney(spreadAnalysis.max_profit)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Max loss</div>
                <div className="font-mono-tab text-lg loss" data-testid="spread-max-loss">
                  {spreadAnalysis.max_loss == null ? "unbounded" : fmtMoney(Math.abs(spreadAnalysis.max_loss))}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Breakevens</div>
                <div className="font-mono-tab text-sm" data-testid="spread-breakevens">
                  {spreadAnalysis.breakevens?.length ? spreadAnalysis.breakevens.map((b) => `$${b}`).join(" · ") : "—"}
                </div>
              </div>
            </div>
          )}

          {spreadAnalysis?.payoff?.length > 0 && (
            <div className="mb-3" data-testid="spread-payoff-chart">
              <svg viewBox="0 0 600 120" className="w-full h-24">
                {(() => {
                  const pts = spreadAnalysis.payoff;
                  const xs = pts.map((p) => p.S);
                  const ys = pts.map((p) => p.pnl);
                  const xMin = Math.min(...xs), xMax = Math.max(...xs);
                  const yMin = Math.min(...ys, 0), yMax = Math.max(...ys, 0);
                  const yRange = yMax - yMin || 1;
                  const zeroY = 10 + (yMax / yRange) * 100;
                  const toX = (v) => 10 + ((v - xMin) / (xMax - xMin)) * 580;
                  const toY = (v) => 10 + ((yMax - v) / yRange) * 100;
                  let d = "";
                  pts.forEach((p, i) => { d += `${i === 0 ? "M" : "L"} ${toX(p.S).toFixed(1)} ${toY(p.pnl).toFixed(1)} `; });
                  const spotX = toX(spreadAnalysis.spot);
                  return (
                    <>
                      <line x1={10} y1={zeroY} x2={590} y2={zeroY} stroke="hsl(var(--border))" strokeDasharray="3 3" />
                      <line x1={spotX} y1={10} x2={spotX} y2={110} stroke="hsl(var(--ai))" strokeDasharray="2 3" opacity="0.6" />
                      <text x={spotX + 3} y={16} fill="hsl(var(--ai))" fontSize="9" fontFamily="JetBrains Mono">spot ${spreadAnalysis.spot.toFixed(2)}</text>
                      <path d={d} stroke="hsl(var(--foreground))" strokeWidth="1.5" fill="none" />
                      {spreadAnalysis.breakevens?.map((b, bi) => (
                        <g key={bi}>
                          <line x1={toX(b)} y1={zeroY - 4} x2={toX(b)} y2={zeroY + 4} stroke="hsl(var(--foreground))" strokeWidth="1.5" />
                          <text x={toX(b)} y={zeroY - 6} textAnchor="middle" fill="hsl(var(--muted-foreground))" fontSize="8">${b}</text>
                        </g>
                      ))}
                    </>
                  );
                })()}
              </svg>
              <div className="text-[10px] text-muted-foreground text-center">Payoff at expiration</div>
            </div>
          )}

          <div className="text-[11px] text-muted-foreground flex gap-2 items-center justify-between">
            <div className="flex gap-2 items-center">
              <Info className="w-3.5 h-3.5 shrink-0" />
              Paper-executes the whole ticket at chain mid — cash settles on the NET premium.
            </div>
            <Button
              data-testid="spread-place"
              disabled={placingSpread || !legs.length}
              onClick={async () => {
                setPlacingSpread(true);
                try {
                  const body = {
                    symbol,
                    notes: "",
                    legs: legs.map((L) => ({
                      kind: L.kind, strike: L.strike, action: L.action,
                      qty: L.qty, price: L.price,
                      contract_symbol: L.contract_symbol, expiration: L.expiration,
                    })),
                  };
                  const { data: res } = await api.post("/options/spread/place", body);
                  toast.success(`${res.label} placed — ${res.cash_delta < 0 ? "paid" : "received"} $${Math.abs(res.cash_delta).toFixed(2)}`);
                  clearLegs();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Spread placement failed");
                } finally {
                  setPlacingSpread(false);
                }
              }}
              className="rounded-full bg-[hsl(var(--gain))] hover:bg-[hsl(var(--gain))]/90 text-black text-xs px-4">
              {placingSpread ? "Placing…" : `Place ${legs.length}-leg spread`}
            </Button>
          </div>
        </Card>
      )}

      {selected && (
        <Card className="p-5 border-border rounded-xl sticky bottom-4 md:relative md:bottom-auto" data-testid="options-ticket">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-semibold">
              {symbol} {selected.row.strike} {selected.side === "C" ? "Call" : "Put"} · {expiration}
            </div>
            <button onClick={() => setSelected(null)} className="text-muted-foreground text-sm">Clear</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
            <div><div className="text-xs text-muted-foreground uppercase">Δ Delta</div><div className="font-mono-tab">{selected.row.delta.toFixed(3)}</div></div>
            <div><div className="text-xs text-muted-foreground uppercase">Γ Gamma</div><div className="font-mono-tab">{selected.row.gamma.toFixed(4)}</div></div>
            <div><div className="text-xs text-muted-foreground uppercase">Θ Theta/day</div><div className="font-mono-tab">{selected.row.theta.toFixed(3)}</div></div>
            <div><div className="text-xs text-muted-foreground uppercase">V Vega</div><div className="font-mono-tab">{selected.row.vega.toFixed(3)}</div></div>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <Label>Contracts</Label>
              <Input type="number" min="1" step="1" value={qty} onChange={(e) => setQty(e.target.value)}
                     data-testid="options-qty" className="mt-1 font-mono-tab" />
              <div className="text-[11px] text-muted-foreground mt-1">
                Est. total: <span className="font-mono-tab">${(((selected.row.bid + selected.row.ask) / 2 || selected.row.last) * 100 * Number(qty || 0)).toFixed(2)}</span>
              </div>
            </div>
            <div>
              <Label>Route</Label>
              <Tabs value={route} onValueChange={setRoute} className="mt-1">
                <TabsList className="grid grid-cols-2 w-full">
                  <TabsTrigger value="sim">Sim</TabsTrigger>
                  <TabsTrigger value="ibkr_paper">IBKR Paper</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={() => setReviewSide("buy")} disabled={placing}
                    data-testid="options-buy"
                    className="rounded-full bg-[hsl(var(--gain))] hover:bg-[hsl(var(--gain))]/90 text-black">
              Review buy
            </Button>
            <Button onClick={() => setReviewSide("sell")} disabled={placing}
                    data-testid="options-sell"
                    className="rounded-full bg-[hsl(var(--loss))] hover:bg-[hsl(var(--loss))]/90 text-black">
              Review sell
            </Button>
          </div>
          <div className="mt-3 text-[11px] text-muted-foreground flex gap-2">
            <Info className="w-3.5 h-3.5 shrink-0" />
            Each contract = 100 shares. Multi-leg spreads coming soon.
          </div>
        </Card>
      )}

      {selected && (
        <AlertDialog open={reviewSide !== null} onOpenChange={(o) => !o && setReviewSide(null)}>
          <AlertDialogContent data-testid="options-review-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Review {reviewSide} option</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="pt-2 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Contract</span><span className="font-mono-tab text-foreground">{symbol} {selected.row.strike} {selected.side === "C" ? "Call" : "Put"}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Expiration</span><span className="font-mono-tab text-foreground">{expiration}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Qty (contracts)</span><span className="font-mono-tab text-foreground">{qty}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Mid premium</span><span className="font-mono-tab text-foreground">${(((selected.row.bid + selected.row.ask) / 2 || selected.row.last)).toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Est. total</span><span className="font-mono-tab text-foreground">${estPremium.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Route</span><span className="font-mono-tab text-foreground">{route === "sim" ? "Simulated" : "IBKR Paper"}</span></div>
                  <div className="pt-2 text-xs text-muted-foreground">You have 8 seconds to tap Undo after this fills.</div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="options-review-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                data-testid="options-review-confirm"
                className={reviewSide === "buy"
                  ? "bg-[hsl(var(--gain))] hover:bg-[hsl(var(--gain))]/90 text-black"
                  : "bg-[hsl(var(--loss))] hover:bg-[hsl(var(--loss))]/90 text-black"}
                onClick={() => submit(reviewSide)}
              >
                Confirm {reviewSide}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
